/* Created by Interactive 63 - https://interactive63.com */
/**
 * Pure logic behind the pivot rulers: distances from a pivot to the edges of a box, whether
 * they are equal (centred), mapping boxes between coordinate frames, and choosing which shape
 * under the pivot to measure. The overlay draws the results; it does not do this maths.
 */
import { applyMatrix, invertMatrix, type Matrix, type Point } from './geometry';

export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface Measurement {
  left: number;
  right: number;
  up: number;
  down: number;
  centredX: boolean;
  centredY: boolean;
}

export const CENTRED_TOLERANCE = 0.05;

/** Edges closer than this to the pivot are treated as touching it, not as something ahead. */
const EDGE_EPSILON = 1e-6;

/** The distance to the nearest edge in each direction; a direction with nothing ahead is absent. */
export interface NearestEdges {
  left?: number;
  right?: number;
  up?: number;
  down?: number;
}

/**
 * Casts four rays from the pivot and returns how far each travels before it meets an edge of any
 * box. A box counts when the ray passes through its span, so a box the pivot sits inside is hit
 * where the ray leaves it.
 */
export function nearestEdges(pivot: Point, boxes: readonly Box[]): NearestEdges {
  const hits: NearestEdges = {};
  /** Keeps the smaller distance for a direction. */
  const keep = (direction: keyof NearestEdges, distance: number): void => {
    if (distance <= EDGE_EPSILON) return;
    const current = hits[direction];
    if (current === undefined || distance < current) hits[direction] = distance;
  };
  for (const box of boxes) {
    if (pivot.y >= box.top && pivot.y <= box.bottom) {
      for (const x of [box.left, box.right]) {
        keep('left', pivot.x - x);
        keep('right', x - pivot.x);
      }
    }
    if (pivot.x >= box.left && pivot.x <= box.right) {
      for (const y of [box.top, box.bottom]) {
        keep('up', pivot.y - y);
        keep('down', y - pivot.y);
      }
    }
  }
  return hits;
}

/**
 * Distances from a pivot to the edges of a box, with centred flags. A distance is negative when
 * the pivot lies outside the box on that side.
 */
export function measureFromPivot(
  pivot: Point,
  box: Box,
  tolerance = CENTRED_TOLERANCE,
): Measurement {
  const left = pivot.x - box.left;
  const right = box.right - pivot.x;
  const up = pivot.y - box.top;
  const down = box.bottom - pivot.y;
  return {
    left,
    right,
    up,
    down,
    centredX: Math.abs(left - right) <= tolerance,
    centredY: Math.abs(up - down) <= tolerance,
  };
}

/**
 * Maps a box from one coordinate space into another. `from` and `to` are each space's matrix to
 * a shared space (typically the screen). The result is the axis-aligned box, in the target
 * frame, around the mapped corners.
 */
export function boxInFrame(box: Box, from: Matrix, to: Matrix): Box {
  const inverse = invertMatrix(to);
  if (!inverse) return box;
  const corners = [
    { x: box.left, y: box.top },
    { x: box.right, y: box.top },
    { x: box.right, y: box.bottom },
    { x: box.left, y: box.bottom },
  ].map((corner) => applyMatrix(inverse, applyMatrix(from, corner)));
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  return {
    left: Math.min(...xs),
    top: Math.min(...ys),
    right: Math.max(...xs),
    bottom: Math.max(...ys),
  };
}

export interface TargetCandidate {
  id: string;
  /** How many ancestors the shape has; larger is deeper. */
  depth: number;
  /** Whether the pivot lies inside the shape. */
  contains: boolean;
  /** Whether the shape belongs to the selected layer. */
  insideLayer: boolean;
}

/**
 * Chooses the shape to measure under the pivot: the deepest shape inside the layer that contains
 * it, otherwise the topmost shape that does. Candidates are listed topmost first.
 */
export function pickTargetUnderPivot(candidates: readonly TargetCandidate[]): string | undefined {
  let best: TargetCandidate | undefined;
  for (const candidate of candidates) {
    if (!candidate.insideLayer || !candidate.contains) continue;
    if (!best || candidate.depth > best.depth) best = candidate;
  }
  if (best) return best.id;
  return candidates.find((c) => c.contains)?.id;
}

/** Formats a distance in document units to one decimal place. */
export function formatDistance(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return (Object.is(rounded, -0) ? 0 : rounded).toFixed(1);
}
